import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Info } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

const TYPES = [
  { id: "starter", label: "Starter site", base: 249, pages: 5, hint: "A professional presence" },
  { id: "business", label: "Business site", base: 349, pages: 10, hint: "Built to win customers" },
  { id: "professional", label: "Professional", base: 599, pages: 15, hint: "Advanced features & CMS" },
  { id: "ecommerce", label: "Online store", base: 1999, pages: 10, hint: "Sell products online" },
];

const ADDONS = [
  { id: "ecommerce", label: "E-commerce", price: 600, hideFor: "ecommerce" },
  { id: "booking", label: "Booking system", price: 350 },
  { id: "blog", label: "Blog / news", price: 150 },
  { id: "seo", label: "SEO boost", price: 199 },
  { id: "custom", label: "Custom features", price: 400 },
];

const MONTHLY = [
  { id: "hosting", label: "Hosting", price: 20 },
  { id: "maintenance", label: "Maintenance (Essential)", price: 29 },
];

export const QuoteCalculator = () => {
  const [type, setType] = useState("business");
  const [pages, setPages] = useState(10);
  const [addons, setAddons] = useState([]);
  const [monthly, setMonthly] = useState(["hosting"]);

  const t = TYPES.find((x) => x.id === type);
  const toggle = (list, setList, id) => setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const { oneOff, perMonth } = useMemo(() => {
    let total = t.base + Math.max(0, pages - t.pages) * 60;
    ADDONS.forEach((a) => {
      if (addons.includes(a.id) && a.hideFor !== type) total += a.price;
    });
    let m = 0;
    MONTHLY.forEach((x) => { if (monthly.includes(x.id)) m += x.price; });
    return { oneOff: total, perMonth: m };
  }, [t, pages, addons, monthly, type]);

  return (
    <section id="calculator" data-testid="quote-calculator-section" className="relative py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <SectionHeading
            overline="Instant estimate"
            title="Build your own quote in 30 seconds."
            sub="Select what you need and get an estimated starting price instantly — no email required."
          />
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-14 bg-white border border-slate-200 soft-shadow rounded-3xl p-6 sm:p-10 grid lg:grid-cols-[1fr_360px] gap-10">
            <div className="space-y-9">
              <div>
                <p className="font-mono2 text-xs tracking-widest uppercase text-slate-400 mb-4">1 · Website type</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" role="radiogroup" aria-label="Website type">
                  {TYPES.map((x) => (
                    <button key={x.id} role="radio" aria-checked={type === x.id}
                      data-testid={`calc-type-${x.id}`}
                      onClick={() => { setType(x.id); setPages(x.pages); }}
                      className={`rounded-2xl border p-4 text-left transition-[border-color,background-color] duration-200 ${
                        type === x.id ? "border-[#0055FF] bg-[#F0F4FF]" : "border-slate-200 hover:border-slate-400 bg-white"
                      }`}>
                      <p className="text-sm font-semibold text-slate-900">{x.label}</p>
                      <p className="text-[11px] text-slate-500 mt-1">{x.hint}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-4">
                  <p className="font-mono2 text-xs tracking-widest uppercase text-slate-400">2 · Number of pages</p>
                  <span className="font-mono2 text-sm text-cyan-600" data-testid="calc-pages-value">{pages} pages</span>
                </div>
                <input type="range" min="1" max="30" value={pages}
                  data-testid="calc-pages-slider"
                  aria-label="Number of pages"
                  onChange={(e) => setPages(Number(e.target.value))}
                  className="w-full accent-[#0055FF] h-2 cursor-pointer" />
                <div className="flex justify-between text-[11px] text-slate-400 mt-1.5 font-mono2"><span>1</span><span>30</span></div>
              </div>
              <div>
                <p className="font-mono2 text-xs tracking-widest uppercase text-slate-400 mb-4">3 · Extra features (one-off)</p>
                <div className="flex flex-wrap gap-2.5">
                  {ADDONS.filter((a) => a.hideFor !== type).map((a) => (
                    <button key={a.id} aria-pressed={addons.includes(a.id)}
                      data-testid={`calc-addon-${a.id}`}
                      onClick={() => toggle(addons, setAddons, a.id)}
                      className={`rounded-full border px-4 py-2 text-sm transition-[border-color,background-color,color] duration-200 ${
                        addons.includes(a.id) ? "border-cyan-600 bg-cyan-50 text-cyan-700" : "border-slate-200 text-slate-600 hover:border-slate-400"
                      }`}>
                      {a.label} <span className="text-slate-400">+£{a.price}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-mono2 text-xs tracking-widest uppercase text-slate-400 mb-4">4 · Ongoing care (monthly)</p>
                <div className="flex flex-wrap gap-2.5">
                  {MONTHLY.map((m) => (
                    <button key={m.id} aria-pressed={monthly.includes(m.id)}
                      data-testid={`calc-monthly-${m.id}`}
                      onClick={() => toggle(monthly, setMonthly, m.id)}
                      className={`rounded-full border px-4 py-2 text-sm transition-[border-color,background-color,color] duration-200 ${
                        monthly.includes(m.id) ? "border-[#0055FF] bg-[#F0F4FF] text-blue-700" : "border-slate-200 text-slate-600 hover:border-slate-400"
                      }`}>
                      {m.label} <span className="text-slate-400">+£{m.price}/mo</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="lg:border-l lg:border-slate-100 lg:pl-10 flex flex-col justify-center">
              <p className="font-mono2 text-xs tracking-widest uppercase text-slate-400">Your estimate</p>
              <AnimatePresence mode="wait">
                <motion.p key={oneOff} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}
                  data-testid="calc-estimate-value"
                  className="mt-3 font-display text-5xl sm:text-6xl font-semibold tracking-tighter text-slate-900">
                  £{oneOff.toLocaleString()}
                </motion.p>
              </AnimatePresence>
              <p className="mt-1.5 text-sm text-slate-500">one-off, starting from</p>
              {perMonth > 0 && (
                <p className="mt-3 font-mono2 text-lg text-cyan-600" data-testid="calc-monthly-value">+ £{perMonth}/month</p>
              )}
              <div className="mt-6 flex items-start gap-2 text-xs text-slate-400 leading-relaxed">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                Estimated price — final quotes depend on project requirements.
              </div>
              <a href="#contact" data-testid="calc-quote-cta"
                className="mt-8 inline-flex items-center justify-center gap-2 bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold text-sm px-6 py-3.5 rounded-full transition-colors duration-200 glow-blue group">
                Get my exact quote
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
